import * as React from 'react'

import {
  IconAddressBook,
  IconChartBar,
  IconChevronRight,
  IconFileTextSpark,
  IconMail,
  IconSettings,
  IconShieldLock,
  IconSmartHome,
} from '@tabler/icons-react'
import { Link, useLocation } from '@tanstack/react-router'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/sui/collapsible'
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/sui/sidebar'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { useHasFeatureAccess } from '@/hooks/use-has-feature-access'
import { PLANS } from '@/lib/plans'
import { useContextStore } from '@/stores/context'

import { SidebarMenuButtonWithTooltip } from '../sidebar-menu-button-with-tooltip'

import locales from './locales'

type NavItem = {
  title: string
  url: string
  icon: React.ComponentType<{ className?: string }>
  isActive?: boolean
  items?: Array<{
    title: string
    url: string
  }>
}

export function NavMain() {
  const { t } = useComponentTranslation(locales)
  const location = useLocation()
  const { setOpenMobile } = useSidebar()

  // Templates are plan-gated. A managed account without access can't act on
  // the upsell, so the entry is hidden for it; retail keeps it as an upsell
  // surface. The /templates route itself stays reachable.
  const hasTemplatesAccess = useHasFeatureAccess(PLANS.ONE)
  const isManaged = useContextStore(
    (store) => store.userAccountContext?.accountInformation.isManaged ?? false,
  )
  const showTemplates = hasTemplatesAccess || !isManaged

  const items: Array<NavItem> = [
    {
      title: t('home'),
      url: '/',
      icon: IconSmartHome,
    },
    {
      title: t('vaults'),
      url: '/vaults',
      icon: IconShieldLock,
    },
    {
      title: t('envelopes'),
      url: '/envelopes',
      icon: IconMail,
    },
    ...(showTemplates
      ? [
          {
            title: t('templates'),
            url: '/templates',
            icon: IconFileTextSpark,
          },
        ]
      : []),
    {
      title: t('contacts'),
      url: '/contacts',
      icon: IconAddressBook,
    },
    {
      title: t('reports'),
      url: '/reports',
      icon: IconChartBar,
    },
    {
      title: t('settings'),
      url: '/settings',
      icon: IconSettings,
    },
  ]

  const isActive = (itemUrl: string) => {
    if (itemUrl === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(itemUrl)
  }

  return (
    <SidebarGroup className="pt-1">
      <SidebarMenu>
        {items.map((item) =>
          item.items ? (
            <Collapsible
              key={item.title}
              defaultOpen={item.isActive}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger
                  render={<SidebarMenuButton tooltip={item.title} />}
                >
                  <item.icon />
                  <span>{item.title}</span>
                  <IconChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton
                          render={
                            <Link
                              to={subItem.url}
                              onClick={() => setOpenMobile(false)}
                            />
                          }
                        >
                          <span>{subItem.title}</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          ) : (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButtonWithTooltip
                render={
                  <Link to={item.url} onClick={() => setOpenMobile(false)} />
                }
                tooltip={item.title}
                isActive={isActive(item.url)}
              >
                <item.icon />
                <span>{item.title}</span>
              </SidebarMenuButtonWithTooltip>
            </SidebarMenuItem>
          ),
        )}
      </SidebarMenu>
    </SidebarGroup>
  )
}
